import React, { useState, useEffect } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  Button,
  Col,
  Row,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import DataTable from "react-data-table-component"; // Importing DataTable
import TableService from "../../Services/QRCodeServices/TableSerivce/TableService";
import AddTableModal from "./AddTableModal";
import EditTableModal from "./EditTableModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import toast from "react-hot-toast";

export const TableManagement = () => {
  const [tables, setTables] = useState([]);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [currentTable, setCurrentTable] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [loading, setLoading] = useState(false); // Added loading state

  // State for filter options
  const [isOccupiedFilter, setIsOccupiedFilter] = useState("all");
  const [isActiveFilter, setIsActiveFilter] = useState("all");

  const [formData, setFormData] = useState({
    identification: "",
    description: "",
    isActive: true,
    isOccupied: false,
  });

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await TableService.getList();
      setTables(response);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error(error.response?.data || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setFormData({
        identification: "",
        description: "",
        isActive: true,
        isOccupied: false,
      });
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setCurrentTable(null);
      setFormData({
        identification: "",
        description: "",
        isActive: true,
        isOccupied: false,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (data) => {
    try {
      await TableService.create(data);
      await fetchTables();
      toggleModal();
      toast.success("Table created successfully");
    } catch (error) {
      console.error("Error creating table:", error);
      toast.error(error.response?.data || "An error occurred");
    }
  };

  const handleEdit = (table) => {
    setCurrentTable(table);
    setFormData({
      identification: table.identification,
      description: table.description,
      isActive: table.isActive,
      isOccupied: table.isOccupied,
    });
    setEditModal(true);
  };

  const handleUpdate = async (data) => {
    try {
      if (currentTable?.id) {
        await TableService.update(currentTable.id, data);
        await fetchTables();
        toggleEditModal();
        toast.success("Table updated successfully");
      }
    } catch (error) {
      console.error("Error updating table:", error);
      toast.error(error.response?.data || "An error occurred");
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedDeleteItem) {
      try {
        await TableService.delete(selectedDeleteItem);
        await fetchTables();
        setDeleteModal(false);
        toast.success("Table deleted successfully");
      } catch (error) {
        console.error("Error deleting table:", error);
        toast.error(error.response?.data || "An error occurred");
      }
    }
  };

  // Filter tables based on selected values
  const filteredTables = tables.filter((table) => {
    return (
      (isOccupiedFilter === "all" ||
        table.isOccupied.toString() === isOccupiedFilter) &&
      (isActiveFilter === "all" || table.isActive.toString() === isActiveFilter)
    );
  });

  // Custom styles for DataTable
  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  // Define columns for DataTable
  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Identification",
      selector: (row) => row.identification,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: "Active",
      selector: (row) => (row.isActive ? "Yes" : "No"),
      sortable: true,
      cell: (row) => (
        <span
          className={`badge ${
            row.isActive ? "bg-success" : "bg-danger"
          }-subtle text-uppercase text-dark`}
        >
          {row.isActive ? "Yes" : "No"}
        </span>
      ),
    },
    {
      name: "Occupied",
      selector: (row) => (row.isOccupied ? "Yes" : "No"),
      sortable: true,
      cell: (row) => (
        <span
          className={`badge ${
            row.isOccupied ? "bg-warning" : "bg-info"
          }-subtle text-uppercase text-dark`}
        >
          {row.isOccupied ? "Yes" : "No"}
        </span>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-1">
          <Button
            color="btn btn-soft-warning"
            className="btn-sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            color="btn btn-soft-danger"
            className="btn-sm"
            onClick={() => {
              setSelectedDeleteItem(row.id);
              setDeleteModal(true);
            }}
          >
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Table List" pageTitle="Table Management" />
        </div>

        {/* Filters */}
        <Row className="mb-3">
          <Col md={4}></Col>
          <Col md={3}>
            <FormGroup>
              <Label for="isOccupiedFilter">Filter by Occupied</Label>
              <Input
                type="select"
                id="isOccupiedFilter"
                value={isOccupiedFilter}
                onChange={(e) => setIsOccupiedFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="true">Occupied</option>
                <option value="false">Not Occupied</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="isActiveFilter">Filter by Active</Label>
              <Input
                type="select"
                id="isActiveFilter"
                value={isActiveFilter}
                onChange={(e) => setIsActiveFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Input>
            </FormGroup>
          </Col>
          <Col
            md={2}
            className="d-flex justify-content-end align-items-end mb-3"
          >
            <Button onClick={toggleModal}>Add Table</Button>
          </Col>
        </Row>

        <Row>
          <DataTable
            columns={columns}
            data={filteredTables}
            progressPending={loading}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            highlightOnHover
            responsive
            striped
            noHeader
          />
        </Row>
      </div>

      <AddTableModal
        isOpen={modal}
        toggle={toggleModal}
        onSubmit={handleSubmit}
        formData={formData}
        handleInputChange={handleInputChange}
      />

      <EditTableModal
        isOpen={editModal}
        toggle={toggleEditModal}
        onSubmit={handleUpdate}
        formData={formData}
        handleInputChange={handleInputChange}
      />

      <DeleteModal
        show={deleteModal}
        onCloseClick={() => setDeleteModal(false)}
        onDeleteClick={handleDeleteConfirm}
      />
    </>
  );
};
